import moment from "moment";
import { HTMLAttributes, useState } from "react";

import Icons from "@/ui/Icons";
import cn from "@/utils/tailwind/cn";

type Props = {
  onSelectDate: (date: moment.Moment) => void;
  initialDate?: moment.Moment;
} & HTMLAttributes<HTMLDivElement>;

export const QuickCalendar = ({
  onSelectDate,
  className,
  initialDate,
  ...props
}: Props) => {
  const [currentDate, setCurrentDate] = useState(initialDate || moment());
  const [currentMonth, setCurrentMonth] = useState(initialDate || moment());

  const startOfMonth = currentMonth.clone().startOf("month");
  const startOfWeek = startOfMonth.clone().startOf("week");

  const days = [];
  const day = startOfWeek.clone();
  for (let i = 0; i < 42; i++) {
    days.push(day.clone());
    day.add(1, "day");
  }

  const handlePrevMonth = () => {
    setCurrentMonth((old) => old.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth((old) => old.clone().add(1, "month"));
  };

  const handleSelectDate = (date: moment.Moment) => {
    onSelectDate(date);
    setCurrentDate(date);
  };

  return (
    <div className={cn("bg-white", className)} {...props}>
      <div className="flex justify-between items-center mb-1 ztk-calendar-header">
        <h2 className="text-lg font-semibold w-full">
          {currentMonth.format("MMMM YYYY")}
        </h2>
        <div className="flex items-center gap-2 ztk-calendar-control-buttons">
          <button
            className="p-2 active:outline-none focus:outline-none hover:bg-gray-100 rounded-full"
            onClick={handlePrevMonth}
          >
            <Icons.ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="p-2 active:outline-none focus:outline-none hover:bg-gray-100 rounded-full"
            onClick={handleNextMonth}
          >
            <Icons.ChevronLeft className="w-6 h-6 rotate-180" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center font-medium ztk-calendar-weekdays">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div className="p-1" key={day}>
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 text-center ztk-calendar-days">
        {days.map((day) => {
          const isToday = day.isSame(moment(), "day");
          const isInCurrentMonth = day.month() === currentMonth.month();
          const isInPast = day.isBefore(moment(), "day");
          const isSelected = day.isSame(currentDate, "day");

          const dayStyle = {
            "!text-gray-300 cursor-not-allowed": isInPast,
            "!bg-primary !text-white hover:!bg-primary": isSelected,
            "text-gray-500": !isInCurrentMonth,
            "bg-gray-200 text-gray-600 hover:bg-gray-200": isToday,
          };
          return (
            <button
              className={cn(
                "p-1.5 m-0.5 rounded-full active:outline-none focus:outline-none hover:bg-gray-100",
                dayStyle,
              )}
              disabled={isInPast || isSelected}
              key={day.format("MM-DD-YYYY")}
              onClick={() => handleSelectDate(day)}
            >
              {day.format("D")}
            </button>
          );
        })}
      </div>
    </div>
  );
};
